import { useState, useRef } from 'react';
import CapturaCamara from './CapturaCamara';
import { comprimirImagen } from '../../utils/comprimirImagen';
import useConfiguracion from '../../hooks/useConfiguracion';
import { textoPaso } from '../../utils/textosReporte';

const LIMITE_ARCHIVO_MB = 10;
const LIMITE_REPORTE_MB = 25;
const LIMITE_CANTIDAD = 4;
// Video/audio ya tienen su propio botón de grabar — "Subir archivo" es solo
// para lo que no se puede grabar en el momento: una foto de la galería o un
// documento (PDF).
const TIPOS_ACEPTADOS = 'image/jpeg,image/png,application/pdf';

const formatearMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

const iconoPorTipo = (tipo) => ({
  foto: 'fa-camera', video: 'fa-video', audio: 'fa-microphone', documento: 'fa-file-pdf',
}[tipo] || 'fa-file');

/**
 * Paso 5/5 del reporte: evidencia (README diseño, sección 5.6). Límites de
 * tamaño (10 MB por archivo, 25 MB por reporte) son una decisión de UX del
 * diseño para no trabar la subida en datos móviles — el backend acepta bien
 * más (`MEDIA_CONVERSION.GENERAL_SETTINGS`, 500 MB), no es un límite técnico.
 */
const PasoEvidencia = ({ evidencias, descripcion, locationData, marcaAguaConfig, onCambiar, onContinuar, onVolver }) => {
  const { configuracion } = useConfiguracion();
  const texto = textoPaso(configuracion, 'evidencia', { titulo: 'Evidencia', subtitulo: '' });
  const [capturando, setCapturando] = useState(null); // 'foto' | 'video' | 'audio' | null
  const [errorTamano, setErrorTamano] = useState('');
  const [errorDescripcion, setErrorDescripcion] = useState('');
  const [previsualizacion, setPrevisualizacion] = useState(null); // evidencia en modal, o null
  const inputArchivoRef = useRef(null);
  const textareaRef = useRef(null);

  // HU-015: la descripción pasa a ser obligatoria — la evidencia sigue
  // opcional (así lo pidió el cliente: es el atractivo de la app, pero no
  // todos pueden adjuntarla en el momento). Se valida acá, no en el
  // `onChange` del textarea, para no marcar error mientras la persona recién
  // empieza a escribir.
  //
  // Cuando falta, además del mensaje, se lleva el foco/scroll al campo para
  // que el ciudadano vea de inmediato qué dato falta (antes el aviso quedaba
  // fuera de pantalla y parecía que "no pasaba nada").
  const handleContinuar = () => {
    if (!descripcion.trim()) {
      setErrorDescripcion('Falta la descripción de lo ocurrido — es obligatoria.');
      textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      textareaRef.current?.focus({ preventScroll: true });
      return;
    }
    setErrorDescripcion('');
    onContinuar();
  };

  const pesoTotalMB = evidencias.reduce((acc, e) => acc + e.tamano, 0) / (1024 * 1024);

  // Async por la compresión de fotos (HU-008 Fase 7) — no bloquea la UI,
  // React sigue funcionando normal mientras el <canvas> recodifica.
  const agregarEvidencia = async (item) => {
    if (evidencias.length >= LIMITE_CANTIDAD) {
      setErrorTamano(`Máximo ${LIMITE_CANTIDAD} archivos por reporte.`);
      return;
    }

    // Fotos livianas antes de subir — buena resolución, menos peso real en
    // la red (ver comprimirImagen.js). Video/audio quedan igual, ya los
    // procesa el backend con ffmpeg.
    let blobFinal = item.blob;
    if (item.blob.type?.startsWith('image/')) {
      blobFinal = await comprimirImagen(item.blob);
    }
    const itemFinal = { ...item, blob: blobFinal, tamano: blobFinal.size };

    if (itemFinal.tamano > LIMITE_ARCHIVO_MB * 1024 * 1024) {
      setErrorTamano(`"${item.nombre}" pesa más de ${LIMITE_ARCHIVO_MB} MB — no se puede adjuntar.`);
      return;
    }
    if (pesoTotalMB + itemFinal.tamano / (1024 * 1024) > LIMITE_REPORTE_MB) {
      setErrorTamano(`Ya llegaste al límite de ${LIMITE_REPORTE_MB} MB por reporte.`);
      return;
    }
    setErrorTamano('');
    onCambiar({ evidencias: [...evidencias, { ...itemFinal, id: Date.now() }] });
  };

  const handleCapturado = ({ blob, blobUrl, tipo, duracion }) => {
    setCapturando(null);
    agregarEvidencia({
      nombre: `${tipo}-${Date.now()}.${tipo === 'foto' ? 'jpg' : tipo === 'video' ? 'webm' : 'webm'}`,
      tamano: blob.size,
      tipo,
      blob,
      blobUrl,
      duracion,
    });
  };

  const handleArchivoSeleccionado = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Antes siempre quedaba `tipo: 'archivo'`, que ninguna de las 3 ramas
    // de la miniatura reconocía para una imagen (ni "foto" ni el genérico
    // de audio) — la miniatura quedaba en blanco. Ahora una foto subida
    // desde la galería se comporta igual que una tomada con la cámara
    // (miniatura real, se comprime igual). PDF sí guarda su propio blobUrl
    // para poder verlo adentro del modal de previsualización.
    const esImagen = file.type.startsWith('image/');
    agregarEvidencia({
      nombre: file.name,
      tamano: file.size,
      tipo: esImagen ? 'foto' : 'documento',
      blob: file,
      blobUrl: URL.createObjectURL(file),
    });
  };

  const eliminarEvidencia = (id) => {
    onCambiar({ evidencias: evidencias.filter((e) => e.id !== id) });
  };

  if (capturando) {
    return (
      <CapturaCamara
        modoInicial={capturando}
        locationData={locationData}
        marcaAguaConfig={marcaAguaConfig}
        onCapturado={handleCapturado}
        onCancelar={() => setCapturando(null)}
      />
    );
  }

  return (
    <div className="rw-paso">
      <div className="rw-titulo-fila">
        <h2 className="rw-titulo" style={texto.estilo}>{texto.titulo}</h2>
        <button type="button" className="rw-omitir" onClick={handleContinuar}>Omitir</button>
      </div>
      <p className="rw-subtitulo" style={texto.estilo}>
        {texto.subtitulo || `Agrega fotos, video, audio o un archivo (opcional pero recomendado) — hasta ${LIMITE_CANTIDAD}.`}
      </p>

      {evidencias.length < LIMITE_CANTIDAD && (
        <div className="pe-accesos-grid">
          <button type="button" className="pe-acceso" onClick={() => setCapturando('foto')}>
            <i className="fas fa-camera pe-icono pe-icono-foto"></i><span>Tomar foto</span>
          </button>
          <button type="button" className="pe-acceso" onClick={() => setCapturando('video')}>
            <i className="fas fa-video pe-icono pe-icono-video"></i><span>Grabar video</span>
          </button>
          <button type="button" className="pe-acceso" onClick={() => setCapturando('audio')}>
            <i className="fas fa-microphone pe-icono pe-icono-audio"></i><span>Grabar audio</span>
          </button>
          <button type="button" className="pe-acceso" onClick={() => inputArchivoRef.current?.click()}>
            <i className="fas fa-paperclip pe-icono pe-icono-archivo"></i><span>Foto o PDF</span>
          </button>
        </div>
      )}
      <input
        ref={inputArchivoRef}
        type="file"
        accept={TIPOS_ACEPTADOS}
        style={{ display: 'none' }}
        onChange={handleArchivoSeleccionado}
      />

      {errorTamano && <p className="rw-error">{errorTamano}</p>}

      {evidencias.length > 0 && (
        <>
          <div className="pe-adjuntos-cabecera">
            <span>Adjuntado · {evidencias.length} archivos</span>
            <span>{formatearMB(pesoTotalMB * 1024 * 1024)} MB / {LIMITE_REPORTE_MB} MB</span>
          </div>
          <div className="pe-adjuntos-grid">
            {evidencias.map((ev) => (
              <button
                key={ev.id}
                type="button"
                className="pe-miniatura"
                onClick={() => setPrevisualizacion(ev)}
                aria-label={`Ver ${ev.nombre}`}
              >
                {ev.blobUrl && ev.tipo === 'foto' && <img src={ev.blobUrl} alt={ev.nombre} />}
                {ev.blobUrl && ev.tipo === 'video' && (
                  <>
                    <video src={ev.blobUrl} muted playsInline preload="metadata" />
                    <i className="fas fa-circle-play pe-miniatura-play"></i>
                  </>
                )}
                {(ev.tipo === 'audio' || ev.tipo === 'documento') && (
                  <div className="pe-miniatura-generica">
                    <i className={`fas ${iconoPorTipo(ev.tipo)} pe-icono pe-icono-${ev.tipo}`}></i>
                  </div>
                )}
                <span className="pe-miniatura-badge">
                  {ev.tipo}{ev.duracion ? ` · ${ev.duracion}s` : ''}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  className="pe-miniatura-eliminar"
                  onClick={(e) => { e.stopPropagation(); eliminarEvidencia(ev.id); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); eliminarEvidencia(ev.id); } }}
                  aria-label={`Quitar ${ev.nombre}`}
                >
                  <i className="fas fa-times"></i>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      <label className="rw-label">Descripción de lo ocurrido *</label>
      <textarea
        ref={textareaRef}
        className={`pe-textarea${errorDescripcion ? ' pe-textarea-error' : ''}`}
        rows={3}
        placeholder="Ej. El bache está a media cuadra del semáforo, ocupa todo un carril…"
        value={descripcion}
        onChange={(e) => onCambiar({ descripcion: e.target.value })}
      />
      {errorDescripcion && (
        <p className="rw-error-campo" role="alert">
          <i className="fas fa-circle-exclamation"></i> {errorDescripcion}
        </p>
      )}

      <div className="rw-pie-fijo rw-pie-con-volver">
        <button type="button" className="rw-boton-secundario" onClick={onVolver}>Atrás</button>
        <button type="button" className="rw-boton-primario" onClick={handleContinuar}>Revisar y enviar</button>
      </div>

      {previsualizacion && (
        <div className="pe-preview-fondo" onClick={() => setPrevisualizacion(null)}>
          {/* Foto/video a pantalla completa real (HU-008 Fase 5) — antes el
              modal era una tarjeta chica centrada (420px máx) para
              cualquier tipo; ahora esos dos usan todo el viewport, que es
              donde de verdad importa ver el detalle. Audio/documento siguen
              en tarjeta, no tiene sentido llenar la pantalla con eso. */}
          <div
            className={`pe-preview-contenido ${['foto', 'video'].includes(previsualizacion.tipo) ? 'pe-preview-pantalla-completa' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pe-preview-barra">
              <span>{previsualizacion.nombre}</span>
              <button type="button" onClick={() => setPrevisualizacion(null)} aria-label="Cerrar">
                <i className="fas fa-times"></i>
              </button>
            </div>
            {previsualizacion.tipo === 'foto' && <img src={previsualizacion.blobUrl} alt={previsualizacion.nombre} />}
            {previsualizacion.tipo === 'video' && <video src={previsualizacion.blobUrl} controls autoPlay playsInline />}
            {previsualizacion.tipo === 'audio' && (
              <div className="pe-preview-audio">
                <i className="fas fa-microphone pe-icono pe-icono-audio"></i>
                <p>Nota de audio</p>
                <audio src={previsualizacion.blobUrl} controls autoPlay />
              </div>
            )}
            {previsualizacion.tipo === 'documento' && (
              previsualizacion.blobUrl ? (
                <iframe src={previsualizacion.blobUrl} title={previsualizacion.nombre} className="pe-preview-pdf" />
              ) : (
                <div className="pe-preview-archivo">
                  <i className="fas fa-file-pdf pe-icono pe-icono-documento"></i>
                  <p>{previsualizacion.nombre}</p>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasoEvidencia;
